import {createBrowserRouter, createRoutesFromElements, Navigate, Route} from 'react-router-dom';
import {lazy, ReactElement} from 'react';

import AuthenticatedRoute from './components/auth/AuthenticatedRoute';
import {customersService, locationsService} from './services';
import {ICustomer, newCustomer, newLocation, Roles} from './model';

import {ILocation} from './model/api/locations/location';
import Dashboard from './pages/dashboard/Dashboard';
import {Error404} from './pages/errors';
import ActiveServicesOverview from './pages/nexparkops/active-services-overview/active-services-overview';
import RoleProtectedRoute from './components/auth/RoleProtectedRoute';
import CustomerInfo from "./pages/customers/customer/customerInfo/CustomerInfo";
import CustomerDetailsTab from "./pages/customers/customer/customerInfo/CustomerDetailsTab";
import CustomerLocationsTab from "./pages/customers/customer/customerInfo/CustomerLocationsTab";
import CustomerFeaturesTab from "./pages/customers/customer/customerInfo/CustomerFeaturesTab";
import CustomerAssetsTab from "./pages/customers/customer/customerInfo/CustomerAssetsTab";
import LocationInfo from "./pages/locations/location/LocationInfo";
import LocationDetailsTab from "./pages/locations/location/LocationDetailsTab";
import LocationAssetsTab from "./pages/locations/location/LocationAssetsTab";
import {PermissionsProvider} from "./components/auth/PermissionsProvider";
import CustomerBillingSettingsTab from "./pages/customers/customer/customerInfo/CustomerBillingSettingsTab";
import CustomerBillingInvoicesTab from "./pages/customers/customer/customerInfo/CustomerBillingInvoicesTab";

export type Router = ReturnType<typeof createBrowserRouter>;

const Login = lazy(() => import('./pages/login/Login'));

const Customers = lazy(() => import('./pages/customers/Customers'));
const Locations = lazy(() => import('./pages/locations/Locations'));
const NexparkOps = lazy(() => import('./pages/nexparkops/NexparkOps'));
const LocationsOverview = lazy(() => import('./pages/nexparkops/location-overview/locations-overview'));

const createAppRouter = (appElement: ReactElement) => {
    return createBrowserRouter(
        createRoutesFromElements(
            <Route
                element={appElement}
                errorElement={<Error404/>}
                handle={{
                    crumb: () => 'k-i-home',
                }}
            >
                <Route element={<AuthenticatedRoute requireAuth={false} redirectPath='/customers'/>}>
                    <Route path='/login' element={<Login/>}/>

                    <Route path='*' element={<Navigate to='/login' replace/>}/>
                </Route>

                <Route element={<AuthenticatedRoute requireAuth={true} redirectPath='/login'/>}>
                    <Route element={<PermissionsProvider/>}>

                        <Route
                            path='/dashboard'
                            handle={{
                                crumb: () => 'Dashboard',
                            }}
                        >
                            <Route index element={<Dashboard/>}></Route>
                        </Route>

                        <Route
                            path='/customers'
                            handle={{
                                crumb: () => 'Customers',
                            }}
                        >
                            <Route index element={<Customers/>}></Route>
                            <Route
                                id='customer-info'
                                path=':id'
                                loader={async ({params}) => {
                                    if (params.id === 'new') {
                                        return newCustomer();
                                    }
                                    return customersService.get(params.id)
                                }}
                                handle={{
                                    crumb: (data: ICustomer | null) => {
                                        return data ? data.name : '';
                                    },
                                }}
                                element={<CustomerInfo></CustomerInfo>}
                            >
                                <Route index element={<Navigate to="details" replace/>}/>

                                <Route
                                    path="details"
                                    element={<CustomerDetailsTab/>}
                                    handle={{tabName: 'Details'}}
                                />
                                <Route
                                    path="locations"
                                    element={<CustomerLocationsTab/>}
                                    handle={{tabName: 'Locations'}}
                                />
                                <Route
                                    path="features"
                                    element={<CustomerFeaturesTab/>}
                                    handle={{tabName: 'Features'}}
                                />
                                <Route
                                    path="billing-settings"
                                    element={<CustomerBillingSettingsTab/>}
                                    handle={{tabName: 'Settings'}}
                                />
                                <Route
                                    path="billing-invoices"
                                    element={<CustomerBillingInvoicesTab/>}
                                    handle={{tabName: 'Invoices'}}
                                />
                                <Route
                                    path="assets"
                                    element={<CustomerAssetsTab/>}
                                    handle={{tabName: 'Assets'}}
                                />
                            </Route>
                        </Route>

                        <Route
                            path='/locations'
                            handle={{
                                crumb: () => 'Locations',
                            }}
                        >
                            <Route index element={<Locations/>}/>

                            <Route element={<RoleProtectedRoute allowedRoles={[Roles.Manager, Roles.Admin]}/>}>
                            </Route>
                            <Route
                                id='location-info'
                                path=':id'
                                loader={async ({params}) => {
                                    if (params.id === 'new') {
                                        return newLocation();
                                    }
                                    return locationsService.get(Number(params.id))
                                }}
                                handle={{
                                    crumb: (data: ILocation | null) => {
                                        return data ? data.name : '';
                                    },
                                }}
                                element={<LocationInfo></LocationInfo>}
                            >
                                <Route index element={<Navigate to="details" replace/>}/>

                                <Route
                                    path="details"
                                    element={<LocationDetailsTab/>}
                                    handle={{tabName: 'Details'}}
                                />
                                <Route
                                    path="assets"
                                    element={<LocationAssetsTab/>}
                                    handle={{tabName: 'Assets'}}
                                />
                            </Route>

                        </Route>
                        <Route element={<RoleProtectedRoute allowedRoles={[Roles.Admin]}/>}>
                            <Route
                                path='/nexparkops'
                                element={<NexparkOps/>}
                                handle={{
                                    crumb: () => 'NexPark Ops Panel',
                                }}
                            >
                                <Route index element={<Navigate to='locations-overview' replace/>}/>

                                <Route
                                    path='locations-overview'
                                    element={<LocationsOverview/>}
                                    handle={{
                                        crumb: () => 'Locations Overview',
                                    }}
                                />

                                <Route
                                    path='active-services-overview'
                                    element={<ActiveServicesOverview/>}
                                    handle={{
                                        crumb: () => 'Active Services Overview',
                                    }}
                                />

                            </Route>
                        </Route>

                        <Route path='*' element={<Navigate to='/customers' replace/>}/>
                    </Route>
                </Route>
            </Route>,
        ),
    );
};

export default createAppRouter;
